// A Traiter Hooks

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { aTraiterAPI } from '../api/a-traiter.api';
import { parseDate } from '@/core/utils/date.utils';
import type { 
  ATraiterFilters 
} from '../types/a-traiter.types';

/**
 * Hook to fetch and manage a-traiter expertises
 */
export const useATraiter = (initialFilter?: 'urgent' | string) => {
  const [filters, setFilters] = useState<ATraiterFilters>({
    searchQuery: '',
    sortBy: 'date',
    sortOrder: 'desc',
    activeFilter: initialFilter === 'urgent' ? 'urgent' : 'all'
  });

  // Fetch expertises
  const {
    data: expertisesData,
    isLoading: expertisesLoading,
    error: expertisesError,
    refetch: refetchExpertises
  } = useQuery({
    queryKey: ['expert-a-traiter'],
    queryFn: aTraiterAPI.getATraiter,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Fetch stats
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError
  } = useQuery({
    queryKey: ['expert-stats'],
    queryFn: aTraiterAPI.getStats,
    staleTime: 60 * 1000, // 1 minute
  });

  // Process expertises to add urgency information
  const processedExpertises = useMemo(() => {
    if (!expertisesData?.data?.expertises) return [];

    return expertisesData.data.expertises.map(expertise => {
      let isUrgent = false;
      let urgencyReason = '';
      let daysSinceRv: number | null = null;

      if (expertise.date_du_rv) {
        const rvDate = parseDate(expertise.date_du_rv);
        if (rvDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          rvDate.setHours(0, 0, 0, 0);
          
          daysSinceRv = Math.floor((today.getTime() - rvDate.getTime()) / (1000 * 60 * 60 * 24));
          
          const isPaid = expertise.perception === 'PAYE BANQUE' || 
                        expertise.perception?.toLowerCase().includes('paye') ||
                        expertise.perception?.toLowerCase().includes('payé');
          
          if (daysSinceRv > 2 && !isPaid) {
            isUrgent = true;
            urgencyReason = `${daysSinceRv} jours depuis la visite`;
          }
        }
      }

      // Calculate time since creation
      const timeSince = expertise.date ? (() => {
        const creationDate = parseDate(expertise.date);
        if (!creationDate) return null;
        const days = Math.floor((new Date().getTime() - creationDate.getTime()) / (1000 * 60 * 60 * 24));
        if (days === 0) return "Aujourd'hui";
        if (days === 1) return "1 jour";
        if (days < 7) return `${days} jours`;
        if (days < 30) return `${Math.floor(days / 7)} sem`;
        return `${Math.floor(days / 30)} mois`;
      })() : null;

      return {
        ...expertise,
        isUrgent,
        urgencyReason,
        daysSinceRv,
        timeSince
      };
    });
  }, [expertisesData]);

  // Filter and sort expertises
  const filteredExpertises = useMemo(() => {
    const filtered = processedExpertises.filter(expertise => {
      // Apply search filter
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch = 
          expertise.type?.toLowerCase().includes(query) ||
          expertise.id?.toString().includes(query) ||
          expertise.address?.some(addr => addr.toLowerCase().includes(query)) ||
          expertise.client?.name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Apply status filter
      if (filters.activeFilter !== 'all') {
        const isPaid = expertise.perception === 'PAYE BANQUE' || 
                       expertise.perception?.toLowerCase().includes('preuve virement');
        
        switch (filters.activeFilter) {
          case 'urgent':
            return expertise.isUrgent;
          case 'paid':
            return isPaid;
          case 'unpaid':
            return !isPaid;
          default:
            return true;
        }
      }
      
      return true;
    });
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'date': {
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          comparison = dateB.getTime() - dateA.getTime();
          break;
        }

        case 'date_rv': {
          const rvDateA = parseDate(a.date_du_rv);
          const rvDateB = parseDate(b.date_du_rv);
          if (!rvDateA && !rvDateB) return 0;
          if (!rvDateA) return 1;
          if (!rvDateB) return -1;
          comparison = rvDateB.getTime() - rvDateA.getTime();
          break;
        }

        case 'id':
          comparison = (b.id || 0) - (a.id || 0);
          break;

        default:
          return 0;
      }
      
      // Apply sort order
      return filters.sortOrder === 'asc' ? -comparison : comparison;
    });
    
    return sorted;
  }, [processedExpertises, filters]);

  return {
    // Data
    expertises: filteredExpertises,
    expertName: expertisesData?.data?.expert || '',
    stats: statsData?.data?.stats || null,
    
    // Loading states
    isLoading: expertisesLoading || statsLoading,
    error: expertisesError || statsError,
    
    // Filters
    filters,
    setFilters,
    updateFilter: (key: keyof ATraiterFilters, value: any) => {
      setFilters(prev => ({ ...prev, [key]: value }));
    },
    
    // Actions
    refetch: refetchExpertises
  };
};

/**
 * Hook to handle expertise actions
 */
export const useExpertiseActions = () => {
  const markAsUrgent = async (expertiseId: number) => {
    try {
      await aTraiterAPI.markAsUrgent(expertiseId);
      return { success: true };
    } catch (error) {
      console.error('Error marking as urgent:', error);
      return { success: false, error };
    }
  };

  const updateStatus = async (expertiseId: number, status: string) => {
    try {
      await aTraiterAPI.updateStatus(expertiseId, status);
      return { success: true };
    } catch (error) {
      console.error('Error updating status:', error);
      return { success: false, error };
    }
  };

  return {
    markAsUrgent,
    updateStatus
  };
};